/* Live listening: HLS from object storage, analysed in the browser.
 *
 * The audio path is node -> object storage -> browser. The receiver mints a
 * playlist of presigned URLs and never touches a media byte (R7.2), so a
 * listener costs the VM one signature per playlist refresh and no bandwidth at
 * all. That is the property that makes one small server enough.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type HlsType from 'hls.js'
import { api } from './api'
import { listeningGainDb, SILENT_DBFS, type Levels } from './dsp'
import { md5Base64 } from './md5'

/* hls.js is about two-thirds of this application's JavaScript and is needed
 * only by the Listen tab. Loading it on demand keeps the fleet list -- the page
 * everyone opens, often on a phone on a boat -- from paying for a feature most
 * visits never use. The import happens inside the click handler, so it overlaps
 * with the network round trips it is about to make. */
let hlsModule: typeof HlsType | null = null
export async function loadHls(): Promise<typeof HlsType> {
  if (!hlsModule) hlsModule = (await import('hls.js')).default
  return hlsModule
}

/** The playlist is same-origin and needs the session cookie, which the
 *  browser attaches by default. The segments are presigned URLs on Hetzner:
 *  they must NOT carry credentials, because the bucket's CORS policy does not
 *  allow them and the signature is the authorisation. */
export function hlsXhrSetup(xhr: XMLHttpRequest, resourceUrl: string) {
  try {
    if (new URL(resourceUrl, window.location.href).origin === window.location.origin) {
      xhr.withCredentials = true
    }
  } catch {
    /* relative URL; same-origin by construction */
  }
}

/* How a player retries a stream that broke, shared by sound and picture. See
 * the long note in useLiveAudio: a player that retries at once is what once
 * pinned the receiver at 93% CPU and starved the uploads it was waiting for. */
export const HLS_MAX_RECOVERIES = 6
export const hlsBackoff = (n: number) => Math.min(1000 * 2 ** n, 15000)

export type LiveStatus =
  | 'idle' | 'starting' | 'playing' | 'stalled' | 'error'
  /* The node stopped sending because it was always going to.
   *
   * `live.max_session_seconds` is 1800 on every node, so a stream ends itself
   * after half an hour. Reported as its own state rather than as the stall it
   * looks like from here: "lost the stream" sends somebody to check an uplink
   * that is working exactly as designed. */
  | 'ended'

/* Roughly how long a node will stream before stopping on its own, less a
 * margin. Only used to tell a designed ending from a fault, so it is compared
 * against loosely -- a node restarted mid-session resets its own clock and the
 * worst outcome of guessing wrong here is the wrong sentence, not the wrong
 * behaviour. */
const SESSION_LIMIT_MS = 28 * 60 * 1000

/** A band-pass, in Hz, or null for no filtering at all. */
export interface Band { lo: number; hi: number }

/* Where the filters sit when nothing is being filtered.
 *
 * Wide open rather than bypassed, so "Alt" is the same signal path as every
 * other preset and switching to it cannot introduce a click. 1 Hz rather than
 * the sensor's 20, because "Alt" has to include the infrasound band -- a 10 Hz
 * corner would have quietly attenuated the very thing the Infralyd preset is
 * for, and only when Alt was selected. The top is clamped to just under
 * Nyquist at whatever rate the context is actually running. */
const BAND_OPEN: Band = { lo: 1, hi: 24000 }

/* How long a preset change takes to arrive.
 *
 * Not instant. A step change in a filter's corner frequency is a discontinuity
 * and arrives as a click, which on a hydrophone stream is indistinguishable
 * from something happening in the water -- the one sound this product must
 * never invent. Eighty milliseconds is under the threshold where a person
 * would call it a delay and well over the one where it snaps. */
const BAND_GLIDE_S = 0.08

/** Where a finished recording got to. 'saved' means object storage has it and
 *  the receiver has indexed it; the device always has a copy regardless. */
export type ClipState = 'idle' | 'uploading' | 'saved' | 'failed'

/* Where a recording should sit before it is handed to the Opus encoder.
 *
 * Loud enough that the codec spends its bits on the signal rather than on the
 * gap between the signal and full scale, and quiet enough to leave room for a
 * transient. This is the secondary bound; the peak target below is what
 * actually decides the gain most of the time. */
const REC_TARGET_RMS_DBFS = -26

/* Where the loudest sample seen recently should land.
 *
 * This is the fix for a clip that came back peaking at 0.7 dBFS, which is
 * clipped. The old code aimed RMS at -20 dBFS and ignored crest factor
 * entirely: music and most transient sea sounds run 15-20 dB of peak above
 * RMS, so hitting the RMS target put the peaks at or through full scale. It
 * then measured a single 85 ms frame, so a quiet moment at the instant of the
 * click set the gain for everything that followed.
 *
 * Twelve dB of headroom below full scale. Enough that a transient half as loud
 * again as anything in the measurement window still fits, and cheap: the file
 * is quiet but intact, and quiet is recoverable where clipped is not. This
 * matters more here than it would elsewhere, because an events-tier node never
 * uploads archive audio, so a saved clip is the only copy of that moment that
 * will ever exist.
 */
const REC_TARGET_PEAK_DBFS = -12
const REC_MAX_BOOST_DB = 60

/** One meter tick: the worst the signal did in that slice. */
interface Tick { peak: number; rms: number }

/* What the receiver will accept, and what the file should be called.
 *
 * The keys are the three MIME types `clips:presign` allows; a browser that
 * writes something else would be refused there, so the recorder is offered only
 * these. Safari writes MP4, everything else writes WebM. */
const EXT_FOR_MIME: Record<string, string> = {
  'audio/webm': 'webm', 'audio/mp4': 'm4a', 'audio/ogg': 'ogg',
}

/** `audio/webm;codecs=opus` -> `audio/webm`. */
const baseMime = (m: string | undefined): string =>
  (m ?? 'audio/webm').split(';')[0]!.trim()

/** The worst the signal did across a window, in dBFS.
 *
 *  Worst case rather than the average: a level chosen from the quietest thing
 *  seen is a level that clips on the loudest. Shaped as `Levels` so the shared
 *  listening rule in `dsp.ts` can be asked about a live stream in the same
 *  terms it is asked about a decoded file -- which is what makes the monitor
 *  and the archive land at the same loudness.
 */
function levelsFrom(ticks: Tick[]): Pick<Levels, 'peakDbfs' | 'rmsDbfs' | 'silent'> {
  let peak = 0
  let rms = 0
  for (const t of ticks) {
    if (t.peak > peak) peak = t.peak
    if (t.rms > rms) rms = t.rms
  }
  // Nothing observed, or digital silence. Callers sit at unity rather than
  // inventing a boost for a signal they cannot see.
  if (!(peak > 0)) {
    return { peakDbfs: SILENT_DBFS, rmsDbfs: SILENT_DBFS, silent: true }
  }
  return {
    peakDbfs: 20 * Math.log10(peak),
    rmsDbfs: rms > 0 ? 20 * Math.log10(rms) : SILENT_DBFS,
    silent: false,
  }
}

/** Gain in dB to *record* through, from what the signal has been doing.
 *
 *  Deliberately its own rule rather than `dsp.listeningGainDb`, though the two
 *  agree on every number today. This one decides what is baked into a file that
 *  may be the only copy of that moment; the other decides how loud something is
 *  in a pair of headphones for as long as someone is listening. Folding them
 *  together would let a change to what a listener hears silently rewrite what
 *  is stored, and only one of those is reversible.
 */
function recordGainFrom(ticks: Tick[]): number {
  const l = levelsFrom(ticks)
  if (l.silent) return 0
  const forPeak = REC_TARGET_PEAK_DBFS - l.peakDbfs
  const forRms = REC_TARGET_RMS_DBFS - l.rmsDbfs
  // Whichever demands less. The peak bound is what stops clipping; the RMS
  // bound stops a single click in an otherwise silent window from deciding
  // that the whole recording should be quiet.
  return Math.max(0, Math.min(forPeak, forRms, REC_MAX_BOOST_DB))
}

export interface LiveAudio {
  status: LiveStatus
  error: string | null
  analyser: AnalyserNode | null
  /** Seconds from segment production to playback, as hls.js sees it. R7.5
   *  wants under 3 s steady state, so it is worth showing rather than hoping. */
  latency: number | null
  start: () => void
  stop: () => void
  /** Monitor gain in dB, applied after the analyser so the numbers on screen
   *  stay the true signal. An uncalibrated hydrophone in air runs 50-80 dB
   *  below full scale; at unity it is inaudible and sounds like a fault. */
  gainDb: number
  setGainDb: (db: number) => void
  /** The band a listener is hearing, or null for everything.
   *
   *  Applied after the analyser, so the picture keeps showing the full spectrum
   *  and a person can see what they filtered out. Recording is upstream of it
   *  too: a clip is evidence, and a filter that quietly deleted two thirds of
   *  the only copy is not a trade worth making. */
  band: Band | null
  setBand: (b: Band | null) => void
  /* No Web Audio graph on this stream, so no spectrum, no filters, no gain
   * and no recording -- but sound.
   *
   * Reached on older WebKit, where a natively played HLS element hands
   * createMediaElementSource no decoded data. That combination is uniquely
   * nasty: the element re-routes into a graph that produces nothing, so the
   * clock advances, the player says it is playing, and the speaker is silent.
   * Measured rather than assumed, and when it happens the controls that depend
   * on the graph are removed rather than left present and inert. */
  graphless: boolean
  /* Dashcam: the interesting thing has usually just happened, so the last
     minutes are always being kept in memory and pressing record saves what is
     already there rather than starting from now. */
  recording: boolean
  recordedSeconds: number
  /** Gain the current or most recent recording was captured through, in dB.
   *  Chosen by measurement when Record is pressed and then held fixed, so it
   *  describes the whole file. Subtract it to recover true level. */
  recordGainDb: number
  /** Upload state of the most recent recording. The local download is not
   *  conditional on this -- a failed upload still leaves the operator holding
   *  the audio. */
  clipState: ClipState
  clipError: string | null
  /** Peak reduction the safety limiter applied to the last recording, in dB.
   *  Above about 1 dB the file has stopped being a linear record of what
   *  happened and says so. Normally zero. */
  clipLimitedDb: number
  startRecording: () => void
  stopRecording: () => void
}

export function useLiveAudio(missionId: string | null, stream = ''): LiveAudio {
  const [status, setStatus] = useState<LiveStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null)
  const [latency, setLatency] = useState<number | null>(null)
  /* Twelve dB up by default.
   *
   * Measured on this fleet, live audio decodes at about -56 dBFS RMS with a
   * source right against the sensor and -80 with the room quiet. At unity that
   * is somewhere between very faint and silent, and the node's own report
   * warns that "anyone opening the player and hearing hiss should not conclude
   * the pipeline is broken" -- which is precisely the conclusion a person
   * draws. A default that makes the sea audible is the more useful lie-free
   * starting point, and the slider says what it is doing in dB.
   *
   * Safe to be generous because the gain is followed by a limiter and sits
   * after the analyser, so boosting changes what you hear and never what the
   * meter or the spectrum report. */
  const [gainDb, setGain] = useState(12)
  const gainDbRef = useRef(12)
  /* Whether the twelve above is still a default or has become a decision.
   *
   * Twelve is the fleet's average shortfall, not this minute's. Once real
   * signal has been heard the gain is set from what the signal is actually
   * doing -- via `dsp.listeningGainDb`, the same rule the archive players
   * solve, so the two land at the same loudness -- and then held, so what a
   * listener hears stops depending on how loud the sea happened to be when the
   * page was written.
   *
   * Held rather than tracked, and for the same reason the record gain is
   * held: a gain that follows the signal makes a passing vessel and a flat
   * calm sound equally loud, which is the one thing a person listening is
   * trying to tell apart. Touching the slider ends the automation for good. */
  const autoGainRef = useRef(true)

  const [band, setBandState] = useState<Band | null>(null)
  const bandRef = useRef<Band | null>(null)
  const hpRef = useRef<BiquadFilterNode | null>(null)
  const lpRef = useRef<BiquadFilterNode | null>(null)
  const [graphless, setGraphless] = useState(false)

  const [recording, setRecording] = useState(false)
  const [recordedSeconds, setRecordedSeconds] = useState(0)
  /* The gain this recording is being captured through, in dB.
   *
   * Surfaced rather than kept private because it is the number that makes the
   * file interpretable: the true level of anything in it is what the meter
   * shows minus this. It is chosen by measurement at the moment Record is
   * pressed and then held, so it describes the whole file and not one instant
   * of it. */
  const [recordGainDb, setRecordGainDb] = useState(0)
  const [clipState, setClipState] = useState<ClipState>('idle')
  const [clipError, setClipError] = useState<string | null>(null)
  const [clipLimitedDb, setClipLimitedDb] = useState(0)
  // Mirrored in a ref because onstop -- which names the file -- runs in a
  // closure created before the gain was chosen.
  const recGainDbRef = useRef(0)
  const recGainRef = useRef<GainNode | null>(null)
  /* A rolling five seconds of level history.
   *
   * Kept continuously while listening so that pressing Record costs nothing
   * and waits for nothing. Measuring at the moment of the click would either
   * delay the recording by a second, which defeats the point of a button you
   * press because something just happened, or sample a single frame -- which
   * is the bug this replaces. */
  const ticksRef = useRef<Tick[]>([])
  const meterRef = useRef(0)
  /* The worst gain reduction the safety limiter applied during a recording.
   * Above a decibel or so the file has stopped being a linear record of what
   * happened, and the operator is entitled to know that. */
  const limiterRef = useRef<DynamicsCompressorNode | null>(null)
  const reductionRef = useRef(0)
  // Held as a ref as well as state: startRecording reads it at the instant of
  // the click and must not be re-created every time the analyser changes.
  const analyserRef = useRef<AnalyserNode | null>(null)
  const recRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const tapRef = useRef<MediaStreamAudioDestinationNode | null>(null)
  const recStart = useRef(0)
  const recTimer = useRef(0)

  const hlsRef = useRef<HlsType | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const ctxRef = useRef<AudioContext | null>(null)
  const gainRef = useRef<GainNode | null>(null)

  /* A scheduled recovery attempt, and the watchdog that decides whether sound
   * is actually arriving. Both are cleared by stop(), so a torn-down session
   * can neither resurrect itself nor report on a stream that no longer
   * exists. */
  const retryRef = useRef(0)
  const watchdogRef = useRef(0)

  /* The AudioContext outlives a single listening session.
   *
   * stop() used to close() it, and start() built a new one. A closed context is
   * dead for good, so every Listen after the first depended on the browser
   * granting a fresh one -- and on Android the second grant arrives suspended,
   * because the close() issued from that same gesture has not settled. Press
   * Stop, press Listen, and the player ran with no sound: the same silent
   * failure as the suspended-context bug, reached by a different route.
   *
   * One context per hook now, resumed on start, suspended between sessions,
   * and closed only on unmount. Suspend and resume are the operations a browser
   * expects a play button to perform; close is for leaving the page. The nodes
   * are per-session and are disconnected on stop, since each session builds its
   * own graph around a new media element. */
  const nodesRef = useRef<AudioNode[]>([])
  /* Set by start(), so that a fragment arriving can clear the recovery budget
   * held in that call's closure. */
  const recoveryReset = useRef<() => void>(() => {})

  /* Which attempt is the current one.
   *
   * `audio.play()` returns a promise that rejects with AbortError when the
   * element is torn down or handed a new source before playback begins --
   * which is exactly what stopping, or pressing Listen twice, does. The
   * rejection arrives after the new attempt has already set its own state, so
   * without this the *previous* attempt's catch would overwrite a healthy
   * "starting" with "playback blocked: AbortError", and the operator would be
   * reading an error about a stream that no longer exists. Every callback
   * below checks that it still owns the generation before touching state. */
  const gen = useRef(0)
  /* When sound first arrived, so a stream that stops after half an hour can be
   * recognised as having finished rather than failed. */
  const playStart = useRef(0)

  /** Move the two corners, gliding rather than stepping. */
  const applyBand = useCallback((b: Band | null, now: number) => {
    const hp = hpRef.current
    const lp = lpRef.current
    if (!hp || !lp) return
    const nyquist = hp.context.sampleRate / 2 - 1
    const lo = b ? Math.max(1, b.lo) : BAND_OPEN.lo
    const hi = Math.min(b ? b.hi : BAND_OPEN.hi, nyquist)
    hp.frequency.setTargetAtTime(lo, now, BAND_GLIDE_S)
    lp.frequency.setTargetAtTime(Math.max(hi, lo + 1), now, BAND_GLIDE_S)
  }, [])

  const setBand = useCallback((b: Band | null) => {
    bandRef.current = b
    setBandState(b)
    const ctx = ctxRef.current
    if (ctx) applyBand(b, ctx.currentTime)
  }, [applyBand])

  const stop = useCallback(() => {
    gen.current++
    window.clearTimeout(retryRef.current)
    window.clearInterval(watchdogRef.current)
    window.clearInterval(meterRef.current)
    ticksRef.current = []
    if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop()
    recRef.current = null
    tapRef.current = null
    window.clearInterval(recTimer.current)
    setRecording(false)
    setRecordedSeconds(0)
    hlsRef.current?.destroy()
    hlsRef.current = null
    audioRef.current?.pause()
    audioRef.current = null
    // Disconnected, not closed. The nodes belong to the session; the context
    // does not, and tearing it down here is what made the second Listen silent.
    nodesRef.current.forEach((n) => { try { n.disconnect() } catch { /* already detached */ } })
    nodesRef.current = []
    void ctxRef.current?.suspend().catch(() => { /* already suspended or closing */ })
    gainRef.current = null
    recGainRef.current = null
    limiterRef.current = null
    hpRef.current = null
    lpRef.current = null
    analyserRef.current = null
    playStart.current = 0
    setAnalyser(null)
    setGraphless(false)
    setLatency(null)
    setStatus('idle')
  }, [])

  // Leaving the page is the one time the context really is finished with.
  useEffect(() => () => {
    stop()
    void ctxRef.current?.close().catch(() => { /* already closed */ })
    ctxRef.current = null
  }, [stop])
  // Switching mission or sensor while listening would otherwise keep the old
  // stream playing under the new controls.
  useEffect(() => {
    stop()
  }, [missionId, stream, stop])

  const start = useCallback(async () => {
    if (!missionId) return
    stop()
    const mine = gen.current
    const current = () => gen.current === mine

    /* AbortError is not a failure the operator can act on. It only ever means
     * "this play() was replaced by another load", which the browser reports
     * with a URL to an article about it -- the least useful thing that can
     * appear on a screen belonging to someone who just wants to hear the sea.
     * The generation check above catches the common cases; this catches the
     * one where the same attempt aborts itself by re-attaching media. */
    const fail = (message: string, e: unknown) => {
      if ((e as { name?: string } | null)?.name === 'AbortError') return
      setError(message)
      setStatus('error')
    }

    setError(null)
    setStatus('starting')

    /* The audio graph is built here, before the first await.
     *
     * A browser only lets an AudioContext start from a user gesture, and an
     * await ends the gesture: everything after `await loadHls()` runs in a
     * fresh task the browser no longer attributes to the click. This code used
     * to create the context after that await, under a comment asserting it was
     * "created inside the click handler" -- which stopped being true the moment
     * the dynamic import was introduced between the two. Desktop Chrome was
     * lenient and it went unnoticed. Android was not: the context was born
     * suspended and nothing ever resumed it.
     *
     * A suspended context is the worst failure available here, because it is
     * silent in both senses. createMediaElementSource re-routes the element
     * away from the speakers and into the graph, so with the graph not running
     * the audio has nowhere to go -- while the element decodes happily,
     * advances currentTime and reports itself as playing. That is exactly the
     * observed symptom: "on air", a moving clock, a flat spectrogram, no sound.
     */
    const audio = new Audio()
    audio.crossOrigin = 'anonymous'
    audio.preload = 'auto'
    audioRef.current = audio

    const ctx = ctxRef.current ?? new AudioContext()
    ctxRef.current = ctx
    // Resumed while the gesture is still live. Resuming a context that is
    // already running is a no-op, so this costs nothing where it was not
    // needed.
    void ctx.resume().catch(() => { /* state is checked below; a throw here is not the signal */ })
    const src = ctx.createMediaElementSource(audio)
    const an = ctx.createAnalyser()
    /* 32768, which is the largest an AnalyserNode allows, and the reason is
     * the bottom of the axis rather than the top.
     *
     * This is physics before it is a preference. Frequency resolution is
     * roughly 1/T, so telling 5 Hz from 10 Hz at all needs a window of a
     * couple of hundred milliseconds, and getting more than one point across
     * that octave needs most of a second. At the old 4096 the bins are 11.7 Hz
     * wide and the entire infrasound band -- the thing juvenile salmon respond
     * to -- falls inside bin zero, so no amount of drawing could have shown
     * it. At 32768 the bins are 1.46 Hz and the band is four of them.
     *
     *     fftSize   Hz/bin   window    bins in 5-10 Hz
     *        4096    11.72     85 ms         1  (the DC term)
     *       16384     2.93    341 ms         3
     *       32768     1.46    683 ms         4
     *
     * The cost is time resolution: a short transient is now spread over two
     * thirds of a second. Paid deliberately. The update rate is unchanged --
     * each call reads the most recent window -- and this strip answers "what is
     * in the water now", not "when exactly did that click happen". The
     * spectrogram components exist for the second question and keep their own
     * settings.
     *
     * Smoothing comes down from 0.5 to 0.3 to give some of the responsiveness
     * back: with a window this long, frame-to-frame averaging on top of it is
     * smoothing something that is already smooth. */
    an.fftSize = 32768
    an.smoothingTimeConstant = 0.3
    an.minDecibels = -110
    an.maxDecibels = 0
    const gain = ctx.createGain()
    gain.gain.value = Math.pow(10, gainDbRef.current / 20)
    gainRef.current = gain

    /* A limiter between the gain and the speaker.
     *
     * The whole point of the gain is to make a signal 60 dB down audible, and
     * the events worth hearing on a hydrophone are transients -- the node
     * measured one 78 dB above its own noise floor. Boosted by the amount that
     * makes the quiet parts audible, that arrives in someone's headphones on a
     * boat. The threshold is high enough that it never touches ordinary
     * listening and only catches what would otherwise hurt.
     *
     * After the analyser, so it shapes what you hear and never what is
     * measured or recorded. */
    const limiter = ctx.createDynamicsCompressor()
    limiter.threshold.value = -6
    limiter.knee.value = 0
    limiter.ratio.value = 20
    limiter.attack.value = 0.002
    limiter.release.value = 0.15

    /* The band-pass, between the analyser and the gain.
     *
     * Two biquads, 12 dB an octave, which is a slope you can hear through
     * rather than a wall. The position is the whole design:
     *
     *   - after the analyser, so the picture keeps showing the full spectrum
     *     and the part being removed is visible rather than merely absent. A
     *     filter you cannot see the effect of is indistinguishable from a sea
     *     that went quiet.
     *   - before the gain, so the automatic level is chosen from the whole
     *     signal and does not jump every time a preset changes.
     *   - nowhere near the recording tap, which hangs off the analyser.
     *
     * Parked wide open. Setting frequencies rather than rewiring is what makes
     * switching presets silent: disconnecting a node mid-stream produces a
     * click, and a click on a hydrophone stream is indistinguishable from
     * something in the water. */
    const hp = ctx.createBiquadFilter()
    hp.type = 'highpass'
    hp.frequency.value = BAND_OPEN.lo
    hp.Q.value = Math.SQRT1_2
    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = Math.min(BAND_OPEN.hi, ctx.sampleRate / 2 - 1)
    lp.Q.value = Math.SQRT1_2
    hpRef.current = hp
    lpRef.current = lp

    src.connect(an)
    an.connect(hp)
    hp.connect(lp)
    lp.connect(gain)
    gain.connect(limiter)
    limiter.connect(ctx.destination)

    /* The recording tap has its own gain, and it is not the monitor's.
     *
     * Three positions were tried and only the third is right.
     *
     * Off the analyser at unity was the original: faithful, and it produced a
     * 138 KB file that plays as silence, because an uncalibrated hydrophone in
     * air sits 50-80 dB below full scale.
     *
     * After the monitor gain fixed the audibility and broke something worse: it
     * baked the slider position into the bytes. Whatever the operator happened
     * to have set became a permanent property of the only copy, and two clips
     * were no longer comparable.
     *
     * Unity is also not the faithful choice it appears to be. This is encoded
     * to Opus, which is lossy and allocates its bits around the signal it is
     * given; a stream 80 dB down is quantised into the codec's own noise floor
     * and cannot be recovered by amplifying afterwards. Recording quiet
     * destroys the data that recording loud preserves.
     *
     * So: a dedicated gain, set from a measurement of the actual signal, held
     * fixed for the whole recording, and written down. The bytes are loud
     * enough for the codec to carry them, the applied figure is recorded so the
     * original level is recoverable by subtraction, and the monitor slider
     * cannot touch any of it. Fixed rather than tracking, because a gain that
     * moves during the recording is not reversible and would make the file a
     * record of nothing in particular. */
    const recGain = ctx.createGain()
    an.connect(recGain)

    /* A brick wall between the capture gain and the encoder.
     *
     * Last resort, not a working part of the chain: with 12 dB of headroom in
     * the gain it should never engage. But the gain is fixed when Record is
     * pressed and the sea is not, so something genuinely louder than anything
     * in the measurement window can still arrive. Limited, that costs a few
     * milliseconds of non-linearity. Clipped, it costs the sample and smears
     * harmonic distortion across the whole spectrum, which would then appear
     * in the spectrogram as signal that was never there.
     *
     * Reduction is watched during recording, so a file that did hit this can
     * say so rather than quietly being something other than a measurement. */
    const recLimit = ctx.createDynamicsCompressor()
    recLimit.threshold.value = -1
    recLimit.knee.value = 0
    recLimit.ratio.value = 20
    recLimit.attack.value = 0.001
    recLimit.release.value = 0.08
    recGain.connect(recLimit)

    const tap = ctx.createMediaStreamDestination()
    recLimit.connect(tap)
    recGainRef.current = recGain
    limiterRef.current = recLimit
    tapRef.current = tap
    nodesRef.current = [src, an, hp, lp, gain, limiter, recGain, recLimit, tap]
    analyserRef.current = an
    setAnalyser(an)
    applyBand(bandRef.current, ctx.currentTime)

    /* The meter, the watchdog and the probe. Installed on every path.
     *
     * It used to be installed only on the hls.js path, because the native
     * branch returned before reaching it. That is precisely backwards: the
     * native branch is the one that can fail silently, and it was the one
     * running without instruments.
     *
     * `onDead` is called at most once, when the measurement below says this
     * stream is being decoded and producing nothing.
     */
    const instrument = (onDead: () => void) => {
      /* Level history, sampled five times a second. Cheap: one
       * getFloatTimeDomainData over 4096 samples, which is the same call the
       * meter and the spectrum already make every frame. */
      const mbuf = new Float32Array(an.fftSize)
      ticksRef.current = []
      /* The probe.
       *
       * Two facts have to be true together, and neither is enough alone:
       *
       *   the clock is advancing   -- audio is genuinely being decoded, so
       *                               this is not merely a stream that has not
       *                               started yet
       *   every sample is exactly 0 -- not "quiet", not "below the floor", but
       *                               bit-exact zero across 4096 samples,
       *                               repeatedly
       *
       * A hydrophone at -80 dBFS is very quiet and is not zero; AAC decoding
       * of sea noise does not produce digital silence. Bit-exact zero while
       * the clock runs means the analyser is not connected to the sound, which
       * is the WebKit behaviour this exists to catch. Requiring both is what
       * stops a genuinely calm sea from being mistaken for a broken graph. */
      let probeTicks = 0
      let sawSignal = false
      let clockMoved = false
      let decided = false

      window.clearInterval(meterRef.current)
      meterRef.current = window.setInterval(() => {
        if (!current()) return
        const el = audioRef.current
        if (!analyserRef.current) return
        an.getFloatTimeDomainData(mbuf)
        let pk = 0
        let sum = 0
        for (let i = 0; i < mbuf.length; i++) {
          const v = mbuf[i]!
          const a = v < 0 ? -v : v
          if (a > pk) pk = a
          sum += v * v
        }
        if (pk > 0) sawSignal = true
        if (el && el.currentTime > 0) clockMoved = true
        const t = ticksRef.current
        t.push({ peak: pk, rms: Math.sqrt(sum / mbuf.length) })
        // Five seconds. Long enough to have seen the loud part of a passing
        // vessel, short enough that a bang ten minutes ago does not hold the
        // gain down for the rest of the session.
        if (t.length > 25) t.shift()
        const lim = limiterRef.current
        if (lim && recRef.current) {
          const r = Math.abs(lim.reduction)
          if (r > reductionRef.current) reductionRef.current = r
        }

        if (decided) return
        probeTicks += 1
        // Seven and a half ticks at 200 ms. Long enough that a slow first
        // fragment is not mistaken for a dead graph.
        if (probeTicks < 8) return
        decided = true
        if (clockMoved && !sawSignal) {
          onDead()
          return
        }
        /* The gain, chosen once from what the signal has actually been doing
         * and then left alone. Skipped entirely if the listener has already
         * moved the slider -- their number is a decision and this one is a
         * guess.
         *
         * The shared listening rule, not the record rule: this number is what
         * a stream sounds like, and the archive players solve for the same
         * target from the same measurement. `neverAttenuate` because a monitor
         * gets one pass at a signal and has the limiter below to catch it,
         * where a stored file can be played again. */
        if (sawSignal && autoGainRef.current) {
          const db = Math.round(
            listeningGainDb(levelsFrom(ticksRef.current), { neverAttenuate: true }))
          autoGainRef.current = false
          gainDbRef.current = db
          setGain(db)
          const g = gainRef.current
          if (g) {
            g.gain.setTargetAtTime(
              Math.pow(10, db / 20), g.context.currentTime, 0.05)
          }
        }
      }, 200)

      /* Is sound actually arriving?
       *
       * currentTime is the honest answer: it advances only when decoded audio
       * is played, so it cannot be fooled by a playlist that loads, a source
       * that is accepted, or an element that is willing. Everything else this
       * component could ask -- readyState, paused, the absence of an error --
       * was true throughout the outage that produced silence.
       *
       * Half a second is a fast enough tick to feel live; two seconds without
       * movement is longer than any segment boundary and short enough that
       * nobody stares at a claim that is no longer true. */
      let lastTime = -1
      let stillFor = 0
      window.clearInterval(watchdogRef.current)
      watchdogRef.current = window.setInterval(() => {
        if (!current()) return
        // Read through the ref: the fallback above replaces the element, and a
        // watchdog holding the abandoned one would report a stream nobody is
        // listening to.
        const t = audioRef.current?.currentTime ?? lastTime
        if (t !== lastTime) {
          lastTime = t
          stillFor = 0
          if (!playStart.current) playStart.current = Date.now()
          setStatus((s) => (s === 'starting' || s === 'stalled' ? 'playing' : s))
          return
        }
        stillFor += 500
        if (stillFor < 2000) return
        // A node streams for half an hour and then stops itself. Past that
        // point silence is the design, and calling it a stall sends somebody
        // to check an uplink that is working.
        if (playStart.current && Date.now() - playStart.current > SESSION_LIMIT_MS) {
          setStatus((s) => (s === 'playing' || s === 'stalled' ? 'ended' : s))
          return
        }
        setStatus((s) => (s === 'playing' ? 'stalled' : s))
      }, 500)
    }

    // One playlist per sensor: the hydrophone's and a HydroMoth's each have
    // their own init segment and can never share one.
    const url = `/v1/missions/${encodeURIComponent(missionId)}/live.m3u8` +
      (stream ? `?stream=${encodeURIComponent(stream)}` : '')

    const Hls = await loadHls()
    // The dynamic import is a round trip, and Stop is reachable during it.
    if (!current()) return

    /* One more attempt, now that the import has settled. Some browsers leave
     * resume() pending until the gesture's task completes, so the state right
     * after the call is not the final answer -- this is the point at which it
     * is. Reported rather than retried silently: a context that will not start
     * is not something the player can fix by trying harder, and the operator
     * needs to know that pressing Listen again is the remedy. */
    if (ctx.state === 'suspended') {
      try { await ctx.resume() } catch { /* state below is the signal */ }
      if (!current()) return
      if (ctx.state === 'suspended') {
        setError('The browser blocked audio playback. Press Listen live again.')
        setStatus('error')
        return
      }
    }

    /* Give up the graph, keep the sound.
     *
     * createMediaElementSource cannot be undone -- once an element is routed
     * into a context it stays routed -- so the element that was captured is
     * abandoned and a second one plays the same URL straight to the speakers.
     * Wasteful by a few hundred kilobytes, and the alternative is a player
     * that is silent on a phone.
     *
     * Everything that depends on the graph goes with it, honestly: no
     * spectrum, no filters, no monitor gain, no recording. The page removes
     * those controls rather than showing them doing nothing. */
    const abandonGraph = () => {
      nodesRef.current.forEach((n) => { try { n.disconnect() } catch { /* detached */ } })
      nodesRef.current = []
      analyserRef.current = null
      hpRef.current = null
      lpRef.current = null
      gainRef.current = null
      tapRef.current = null
      recGainRef.current = null
      limiterRef.current = null
      setAnalyser(null)
      setGraphless(true)
      try { audio.pause() } catch { /* never started */ }
      const plain = new Audio()
      plain.preload = 'auto'
      plain.src = url
      audioRef.current = plain
      void plain.play().catch((e) => { if (current()) fail(String(e), e) })
    }

    if (!Hls.isSupported()) {
      /* Safari plays HLS natively. On some versions a natively played element
       * hands createMediaElementSource no decoded data -- and because the
       * capture has already re-routed the element away from the speakers, that
       * is not merely a dark spectrogram: it is silence, while the clock
       * advances and the player reports itself as playing. At -56 to -80 dBFS
       * a listener has no way to tell that from a quiet sea.
       *
       * So it is measured. `instrument` starts a probe that watches for the
       * exact signature -- the clock moving while every sample is precisely
       * zero -- and falls back if it sees it. */
      audio.src = url
      void audio.play().catch((e) => { if (current()) fail(String(e), e) })
      instrument(abandonGraph)
      return
    }

    const hls = new Hls({
      // 1-second segments with no ENDLIST. Staying near the live edge is the
      // whole point (R7.5: first sound under 5 s), so the buffer is kept small
      // deliberately -- a large one is just added latency here.
      lowLatencyMode: true,
      liveSyncDurationCount: 2,
      maxBufferLength: 6,
      backBufferLength: 12,
      manifestLoadingTimeOut: 12000,
      xhrSetup: hlsXhrSetup,
    })
    hlsRef.current = hls

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      // Note this does not set 'playing'. play() resolving means the element
      // accepted the source, not that a single sample came out of it -- on a
      // live stream whose window has gone stale those are very different
      // things, and reporting the first as the second is what made the panel
      // claim "on air" over silence. The watchdog below promotes the status
      // once the clock actually moves.
      void audio.play()
        .catch((e) => { if (current()) fail(`playback blocked: ${e}`, e) })
    })

    /* Recovery is counted and delayed, never immediate.
     *
     * This used to call hls.startLoad() straight from the error handler. On a
     * live stream whose playlist is briefly unusable -- the node behind on its
     * uploads, so every segment in the window has expired -- that fails again
     * as soon as it is retried, and startLoad() aborts the in-flight playlist
     * request before issuing the next one. The result was a tight loop: one
     * listener on a phone produced 155,000 playlist requests in 45 minutes,
     * ~58 a second, and pinned the two-core receiver at 93% CPU.
     *
     * That is not merely wasteful, it is self-defeating. The same API mints the
     * node's upload URLs, so a stalled player starved the very uploads it was
     * waiting for and the stream could never recover while anyone watched it.
     * A player that backs off is what lets the node catch up.
     *
     * Doubling from 1 s to a 15 s ceiling, six attempts, then it says so and
     * stops. Counters reset on FRAG_CHANGED, so a stream that recovers gets its
     * full budget back and a long session is not slowly rationed. */
    const MAX_RECOVERIES = HLS_MAX_RECOVERIES
    let netTries = 0
    let mediaTries = 0
    const backoff = hlsBackoff
    /* The hls.js detail is shown, not swallowed. "Lost the stream" is not
     * actionable on its own, whereas the difference between a playlist that
     * stopped loading and fragments that will not fetch is the difference
     * between looking at this server and looking at object storage -- and the
     * person reading it is usually the person who can tell someone which. */
    const giveUp = (why: string, what: string) => {
      setError(`${why} (${what}) Nothing was lost — the node keeps recording either way.`)
      setStatus('error')
    }
    const later = (fn: () => void, delay: number) => {
      window.clearTimeout(retryRef.current)
      retryRef.current = window.setTimeout(() => { if (current()) fn() }, delay)
    }
    recoveryReset.current = () => { netTries = 0; mediaTries = 0 }

    hls.on(Hls.Events.ERROR, (_evt, data) => {
      if (!current()) return
      // Compared as strings: hls.js types `details` as a string enum, and
      // comparing an enum member to a literal is a type error even though the
      // runtime values are identical.
      const details = String(data.details)
      if (!data.fatal) {
        // Non-fatal gaps are normal on a 1-second live stream and hls.js
        // recovers from them; surfacing each one as an error would make a
        // working stream look broken.
        if (details === 'bufferStalledError') setStatus('stalled')
        return
      }
      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          if (details === 'manifestLoadError') {
            setError('No playlist. The node may not have live enabled yet.')
            setStatus('error')
          } else if (netTries >= MAX_RECOVERIES) {
            giveUp('Lost the stream and could not pick it up again.', details)
          } else {
            setStatus('stalled')
            later(() => hls.startLoad(), backoff(netTries++))
          }
          break
        case Hls.ErrorTypes.MEDIA_ERROR:
          if (mediaTries >= MAX_RECOVERIES) {
            giveUp('The audio could not be decoded.', details)
          } else {
            setStatus('stalled')
            later(() => hls.recoverMediaError(), backoff(mediaTries++))
          }
          break
        default:
          setError(details || 'stream failed')
          setStatus('error')
      }
    })

    hls.on(Hls.Events.FRAG_CHANGED, () => {
      const l = hls.latency
      if (Number.isFinite(l)) setLatency(l)
      // A fragment played through is the only real evidence the stream works,
      // so it is what returns the recovery budget spent getting here.
      recoveryReset.current()
    })

    instrument(abandonGraph)

    hls.loadSource(url)
    hls.attachMedia(audio)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionId, stream, stop])

  /* Presign, PUT to storage, confirm.
   *
   * The bytes go browser -> object storage and never touch the receiver (R7.2),
   * which is the same path a node's uploads take and the same path playback
   * reads back. What crosses this server is a signature and a row.
   *
   * Failure is reported and never silent. The clip is already on the device by
   * the time this runs, so the honest message is "saved here, not there" --
   * which is actionable, unlike a spinner that stops. */
  const uploadClip = useCallback(
    async (blob: Blob, durationS: number, gainDb: number) => {
      if (!missionId) return
      setClipState('uploading')
      setClipError(null)
      try {
        const bytes = new Uint8Array(await blob.arrayBuffer())
        const md5 = md5Base64(bytes)
        // The start of the audio, not of the click. Everything else in the
        // system indexes on when a sound happened.
        const startedUtc = new Date(Date.now() - durationS * 1000).toISOString()

        const pre = await api.post<{
          clip_id: string; key: string; url: string
          headers: Record<string, string>
        }>(`/v1/missions/${encodeURIComponent(missionId)}/clips:presign`, {
          schema: 1,
          started_utc: startedUtc,
          duration_s: Number(durationS.toFixed(2)),
          size: blob.size,
          md5_b64: md5,
          gain_db: Number(gainDb.toFixed(1)),
          mime: baseMime(blob.type),
        })

        // Not api.put: that is the same-origin JSON helper and would send the
        // session cookie to Hetzner, which the bucket's CORS policy does not
        // allow and which the signature makes unnecessary.
        const put = await fetch(pre.url, {
          method: 'PUT', body: blob, headers: pre.headers,
        })
        if (!put.ok) throw new Error(`storage refused the upload (${put.status})`)

        await api.post(`/v1/clips/${encodeURIComponent(pre.clip_id)}:complete`)
        setClipState('saved')
      } catch (e) {
        setClipError(e instanceof Error ? e.message : String(e))
        setClipState('failed')
      }
    },
    [missionId],
  )

  const startRecording = useCallback(() => {
    const tap = tapRef.current
    if (!tap || recRef.current) return

    /* Choose the capture gain now, and do not touch it again until this
     * recording ends. Set before MediaRecorder starts, so the very first frame
     * is already at the level the rest of the file is at -- a gain applied a
     * moment later would leave the opening of every clip at a different level
     * from its body, which is exactly the kind of quiet inconsistency nobody
     * finds until they are comparing two recordings a year from now. */
    const g = recGainRef.current
    // From the rolling window, so this is instant and is based on seconds of
    // signal rather than on whatever the last 85 ms happened to contain.
    const db = recordGainFrom(ticksRef.current)
    reductionRef.current = 0
    if (g) g.gain.setValueAtTime(Math.pow(10, db / 20), g.context.currentTime)
    recGainDbRef.current = db
    setRecordGainDb(db)
    setClipState('idle')
    setClipError(null)
    setClipLimitedDb(0)
    // Opus in WebM where it is available -- a tenth the size of WAV over a
    // field connection -- and MP4 on Safari, which writes nothing else. Both
    // are types the receiver's presign accepts; offering a fourth would produce
    // a recording it then refuses.
    const mime = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
      .find((m) => MediaRecorder.isTypeSupported(m))
    const rec = new MediaRecorder(tap.stream, mime ? { mimeType: mime } : undefined)
    chunksRef.current = []
    rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data) }
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      // UTC in the name, whatever clock the screen is showing. A file outlives
      // the browser that made it, and the person who opens it next year has no
      // way to know which zone the tab was in.
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
      a.href = url
      // The capture gain, not the monitor slider. The file was recorded
      // through this one, and the true level of anything in it is what the
      // meter showed minus this -- which is only recoverable if the number
      // travels with the file. Rule 5: uncalibrated means relative.
      const g = Math.round(recGainDbRef.current)
      // The extension follows what was actually written. Safari records MP4,
      // and calling that file .webm makes it something a media player refuses
      // to open -- a recording that exists and cannot be played is worse than
      // one that failed loudly.
      a.download = `phoniq-${missionId}-${stamp}-gain${g >= 0 ? '+' : ''}${g}dB`
        + `.${EXT_FOR_MIME[baseMime(rec.mimeType)] ?? 'webm'}`
      a.click()
      // Revoked on a delay: Safari has not always finished reading the blob by
      // the time click() returns, and revoking early produces an empty file.
      setTimeout(() => URL.revokeObjectURL(url), 30_000)
      chunksRef.current = []

      /* The download happens first and unconditionally, above; the upload is
       * attempted after and may fail without taking the recording with it.
       *
       * That ordering is the point. The operator pressed Record because they
       * heard something, and on a farm uplink the upload is the part most
       * likely not to work. A clip that exists only in a bucket it never
       * reached is worse than one sitting in the phone's Downloads folder. */
      const seconds = Math.max(0.1, (performance.now() - recStart.current) / 1000)
      setClipLimitedDb(reductionRef.current)
      void uploadClip(blob, seconds, recGainDbRef.current)
    }
    rec.start(1000)
    recRef.current = rec
    recStart.current = performance.now()
    setRecording(true)
    setRecordedSeconds(0)
    recTimer.current = window.setInterval(
      () => setRecordedSeconds(Math.floor((performance.now() - recStart.current) / 1000)), 500)
  }, [missionId])

  const stopRecording = useCallback(() => {
    window.clearInterval(recTimer.current)
    const rec = recRef.current
    recRef.current = null
    setRecording(false)
    if (rec && rec.state !== 'inactive') rec.stop()
  }, [])

  const setGainDb = useCallback((db: number) => {
    setGain(db)
    gainDbRef.current = db
    // A hand on the slider ends the automation. The measured figure is a good
    // guess about a signal; this is a person saying what they want to hear,
    // and nothing should move it back afterwards.
    autoGainRef.current = false
    // Ramped, not assigned. A step change in gain is a click in the
    // headphones, which at these boost levels is the loudest thing in the
    // session.
    const g = gainRef.current
    if (g) {
      const t = g.context.currentTime
      g.gain.cancelScheduledValues(t)
      g.gain.setTargetAtTime(Math.pow(10, db / 20), t, 0.02)
    }
  }, [])

  return { status, error, analyser, latency, start, stop, gainDb, setGainDb,
           band, setBand, graphless,
           recording, recordedSeconds, recordGainDb, clipState, clipError,
           clipLimitedDb, startRecording, stopRecording }
}
